import React, { useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import apiDefinitions from '../../../../core/api/apiDefinitions';
import { showError, showSuccess } from '../../../../core/alert/sweetAlertConfig';
import { checkPermission } from '../../../../core/permissons/CheckPermissons';

interface VehicleKeyBooking {
  bookingId: number;
  vehicleRegistrationNumber: string;
  vehicleType: string;
  keyCodes: string[];
  keyIds: string[];
  departureTime: string;
  driverName: string;
  driverEmpNo: string;
}

interface VehicleKeySuggestProps {
  data: VehicleKeyBooking[];
  onHandoverSuccess: () => void;
}

const VehicleKeySuggest: React.FC<VehicleKeySuggestProps> = ({ data, onHandoverSuccess }) => {
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showHandoverModal, setShowHandoverModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<VehicleKeyBooking | null>(null);
  const [selectedKeyIds, setSelectedKeyIds] = useState<string[]>([]);
  const [handoverType, setHandoverType] = useState<string>('TEMPORARY');
  const [isLoading, setIsLoading] = useState(false);

  const handleViewClick = (booking: VehicleKeyBooking) => {
    setSelectedBooking(booking);
    setShowDetailsModal(true);
  };

  const handleHandoverClick = (booking: VehicleKeyBooking) => {
    setSelectedBooking(booking);
    setSelectedKeyIds(booking.keyIds.length > 0 ? [booking.keyIds[0]] : []); // Default to first key if available
    setHandoverType('TEMPORARY');
    setShowHandoverModal(true);
  };

  const handleKeyToggle = (keyId: string) => {
    setSelectedKeyIds(prev =>
      prev.includes(keyId)
        ? prev.filter(id => id !== keyId)
        : [...prev, keyId]
    );
  };

  const handleHandoverSubmit = async () => {
    if (!checkPermission('ROLE_MARK_HANDOVER')) {
      showError('You do not have permission to mark key handover.');
      return;
    }

    if (!selectedBooking) {
      showError('No vehicle booking selected.');
      return;
    }

    if (selectedKeyIds.length === 0) {
      showError('Please select at least one key to handover.');
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        keyIds: selectedKeyIds, // Send all selected keyIds
        handoverTo: selectedBooking.driverEmpNo,
        handoverType: handoverType,
      };
      const res = await apiDefinitions.markHandover(payload);
      if (res.status === 200 && res.data.status === 'S1000') {
        showSuccess("Handover marked successfully");
        setShowHandoverModal(false);
        setSelectedBooking(null);
        setSelectedKeyIds([]);
        setHandoverType('TEMPORARY');
        onHandoverSuccess();
      } else {
        showError(res.data?.statusDescription || 'Failed to mark handover.');
      }
    } catch (err: any) {
      showError(err.response?.data?.statusDescription || 'Failed to mark handover. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedBooking(null);
  };

  const handleCloseHandoverModal = () => {
    setShowHandoverModal(false);
    setSelectedBooking(null);
    setSelectedKeyIds([]);
    setHandoverType('TEMPORARY');
  };

  return (
    <>
      <div className="table-responsive">
        <table className="table table-hover">
          <thead>
            <tr>
              <th>Booking ID</th>
              <th>Vehicle Reg. No.</th>
              <th>Vehicle Type</th>
              <th>Departure Time</th>
              <th>Driver Emp. No.</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center">No bookings available.</td>
              </tr>
            ) : (
              data.map((booking) => (
                <tr key={booking.bookingId}>
                  <td>{booking.bookingId}</td>
                  <td>{booking.vehicleRegistrationNumber}</td>
                  <td>{booking.vehicleType}</td>
                  <td>
                    {new Date(booking.departureTime).toLocaleString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: true,
                    })}
                  </td>
                  <td>{booking.driverEmpNo}</td>
                  <td>
                    <button
                      className="btn btn-sm btn-info me-1"
                      onClick={() => handleViewClick(booking)}
                      disabled={isLoading}
                      title="View Details"
                    >
                      <i className="ti ti-eye"></i>
                    </button>
                    <button
                      className="btn btn-sm btn-success"
                      onClick={() => handleHandoverClick(booking)}
                      disabled={isLoading || booking.keyIds.length === 0}
                      title="Handover Keys"
                    >
                      <i className="ti ti-key"></i>
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Details Modal */}
      <Modal show={showDetailsModal} onHide={handleCloseDetailsModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Vehicle Key Booking Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedBooking ? (
            <div>
              <p><strong>Booking ID:</strong> {selectedBooking.bookingId}</p>
              <p><strong>Vehicle Registration Number:</strong> {selectedBooking.vehicleRegistrationNumber}</p>
              <p><strong>Vehicle Type:</strong> {selectedBooking.vehicleType}</p>
              <p><strong>Key Codes:</strong> {selectedBooking.keyCodes.join(', ')}</p>
              <p>
                <strong>Departure Time:</strong>{' '}
                {new Date(selectedBooking.departureTime).toLocaleString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: true,
                })}
              </p>
              <p><strong>Driver Name:</strong> {selectedBooking.driverName}</p>
              <p><strong>Driver Employee Number:</strong> {selectedBooking.driverEmpNo}</p>
            </div>
          ) : (
            <p>No details available.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleCloseDetailsModal} disabled={isLoading}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Handover Modal */}
      <Modal show={showHandoverModal} onHide={handleCloseHandoverModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Handover Vehicle Keys</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Select Keys</Form.Label>
              {selectedBooking && selectedBooking.keyIds.length > 0 ? (
                selectedBooking.keyIds.map((keyId, index) => (
                  <Form.Check
                    key={keyId}
                    type="checkbox"
                    id={`key-${keyId}`}
                    label={`Key Code: ${selectedBooking.keyCodes[index]}`}
                    value={keyId}
                    checked={selectedKeyIds.includes(keyId)}
                    onChange={() => handleKeyToggle(keyId)}
                    disabled={isLoading}
                  />
                ))
              ) : (
                <p>No keys available for this booking.</p>
              )}
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Handover Type</Form.Label>
              <Form.Select
                value={handoverType}
                onChange={(e) => setHandoverType(e.target.value)}
                disabled={isLoading}
              >
                <option value="TEMPORARY">Temporary</option>
                <option value="PERMANENT">Permanent</option>
              </Form.Select>
            </Form.Group>
            {selectedBooking && (
              <div>
                <p><strong>Vehicle:</strong> {selectedBooking.vehicleRegistrationNumber}</p>
                <p><strong>Driver:</strong> {selectedBooking.driverName} ({selectedBooking.driverEmpNo})</p>
              </div>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleCloseHandoverModal} disabled={isLoading}>
            Cancel
          </Button>
          <Button variant="success" onClick={handleHandoverSubmit} disabled={isLoading || selectedKeyIds.length === 0}>
            {isLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Processing...
              </>
            ) : (
              'Handover'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default VehicleKeySuggest;